import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { Mutex } from 'async-mutex';
import i18next from 'i18next';
import { getConfig } from '../services/configService';
import { API_ROOT as BUILD_TIME_API_ROOT } from '../config';
import { logUserOut, refreshTokenReceived } from '../features/auth/authSlice';
import { getCookie } from '../helpers/cookieHelpers';
import { CurrentUser } from '../helpers/CurrentUser';

const mutex = new Mutex();

/**
 * Get API_ROOT from runtime config or fallback to build-time
 * This is called on each request to ensure we use the latest config
 */
const getApiRoot = () => {
  const config = getConfig();
  return config.apiRoot || BUILD_TIME_API_ROOT;
};

/**
 * Create a dynamic baseQuery that uses runtime API_ROOT
 * This ensures each request uses the current config value
 */
const createDynamicBaseQuery = (includeAuth = true) => {
  return async (args, api, extraOptions) => {
    const apiRoot = getApiRoot();
    const baseUrl = `${apiRoot}/backend/api/v1/`;
    
    const baseQuery = fetchBaseQuery({
      baseUrl,
      prepareHeaders: includeAuth ? (headers) => {
        const token = CurrentUser.get()?.token;
        const guestSessionToken = getCookie('guest_session_token');
        if (guestSessionToken) {
          headers.set('Guest-Session-Token', guestSessionToken);
        }
        if (token) {
          headers.set('Authentication-Token', token);
        }
        headers.set('Accept-Language', i18next.language);
        return headers;
      } : undefined,
    });
    
    return baseQuery(args, api, extraOptions);
  };
};

export const authorizedBaseQuery = createDynamicBaseQuery(true);
const baseQuery = createDynamicBaseQuery(false);

export const baseQueryWithReauth = async (args, api, extraOptions) => {
  await mutex.waitForUnlock();

  let result = await authorizedBaseQuery(args, api, extraOptions);
  if (result.error && result.error.status === 401) {
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();
      try {
        const refreshResult = await baseQuery(
          {
            url: 'users/session',
            method: 'PUT',
            body: {
              refresh_token: CurrentUser.get()?.refresh_token,
              user_id: CurrentUser.get()?.user_id,
            },
          },
          api,
          extraOptions,
        );
        if (refreshResult.data) {
          api.dispatch(refreshTokenReceived(refreshResult.data.data));
          result = await authorizedBaseQuery(args, api, extraOptions);
        } else {
          api.dispatch(logUserOut());
        }
      } finally {
        release();
      }
    } else {
      await mutex.waitForUnlock();
      result = await authorizedBaseQuery(args, api, extraOptions);
    }
  }
  return result;
};
